import { ReceiptDuplicateEntry } from "@/components/receipts/ReceiptDuplicateEntry";
// Code splitting optimization - v2024-03-18
import { Switch, Route, useLocation, useSearch } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, hasPermission, useAuth } from "@/lib/auth";
import { useEffect, Suspense, Component, ErrorInfo, ReactNode, useState } from "react";
import { safeLazy as lazy } from "./lib/safe-lazy";
import { receiptPrefill } from "@/lib/purchase-request-receiving";
import Layout from "@/components/layout/Layout";
import SupplierPortal from "@/pages/SupplierPortal";
import MobileLayout from "@/pages/mobile/MobileLayout";
const CashPinGateProvider = lazy(() => import("@/components/branch-cash/OnShift").then(module => ({default:module.CashPinGateProvider})));

const MobileHome = lazy(() => import("@/pages/mobile/MobileHome"));
const MobileInbox = lazy(() => import("@/pages/mobile/MobileInbox"));
const MobileInbound = lazy(() => import("@/pages/mobile/MobileInbound"));
const MobileInboundOrderDetail = lazy(() => import("@/pages/mobile/MobileInboundOrderDetail"));
const MobileInboundOrderPropose = lazy(() => import("@/pages/mobile/MobileInboundOrderPropose"));
const MobileProfile = lazy(() => import("@/pages/mobile/MobileProfile"));
const MobileSync = lazy(() => import("@/pages/mobile/MobileSync"));
const MobileScan = lazy(() => import("@/pages/mobile/MobileScan"));
const MobileDeepLink = lazy(() => import("@/pages/mobile/MobileDeepLink"));
const MobileStocktakeList = lazy(() => import("@/pages/mobile/MobileStocktakeList"));
const MobileStocktakeCount = lazy(() => import("@/pages/mobile/MobileStocktakeCount"));
const MobileReceiveDelivery = lazy(() => import("@/pages/mobile/MobileReceiveDelivery"));
const MobileItems = lazy(() => import("@/pages/mobile/MobileItems"));
const MobileSupplierReceipts = lazy(() => import("@/pages/mobile/MobileSupplierReceipts"));
const MobileSupplierReceiptForm = lazy(() => import("@/pages/mobile/MobileSupplierReceipts").then((module) => ({ default: module.MobileSupplierReceiptForm })));
const MobileSupplierReceiptDetail = lazy(() => import("@/pages/mobile/MobileSupplierReceipts").then((module) => ({ default: module.MobileSupplierReceiptDetail })));
const MobileWasteLogs = lazy(() => import("@/pages/mobile/MobileWaste"));
const MobileWasteAnalytics = lazy(() => import("@/pages/mobile/MobileWaste").then((module) => ({ default: module.MobileWasteAnalytics })));

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import NotFound from "@/pages/not-found";
import ReceiptChanges from "@/pages/ReceiptChanges";
import { isChunkLoadError, reloadForFreshShell } from "@/lib/pwa-update";
import RulesList from "@/pages/admin/RulesList";
import RuleEditor from "@/pages/admin/RuleEditor";
import RulesAudit from "@/pages/admin/RulesAudit";
import RulesFeatureSettings from "@/pages/admin/RulesFeatureSettings";
import BulkOrderUpload from "@/pages/BulkOrderUpload";

const OperationsDashboard = lazy(() => import("@/pages/OperationsDashboard"));
const Inventory = lazy(() => import("@/pages/Inventory"));
const Orders = lazy(() => import("@/pages/Orders"));
const Financials = lazy(() => import("@/pages/Financials"));
const ExpenseSetup = lazy(() => import("@/pages/ExpenseSetup"));
const BranchCash = lazy(() => import("@/pages/BranchCash"));
const MyPersonalFloats = lazy(() => import("@/components/branch-cash/PersonalFloats"));
const Admin = lazy(() => import("@/pages/Admin"));
const PriceChangeReasons = lazy(() => import("@/pages/PriceChangeReasons"));
const AdminMergeDuplicates = lazy(() => import("@/pages/AdminMergeDuplicates"));
const Categories = lazy(() => import("@/pages/Categories"));
const Brands = lazy(() => import("@/pages/Brands"));
const Vehicles = lazy(() => import("@/pages/Vehicles"));
const Settings = lazy(() => import("@/pages/Settings"));
const ProductDetails = lazy(() => import("@/pages/ProductDetails"));
const ItemFormPage = lazy(() => import("@/pages/ItemFormPage"));
const ItemBrandsPage = lazy(() => import("@/pages/ItemBrandsPage"));
const BulkUploadItems = lazy(() => import("@/pages/BulkUploadItems"));
const BulkUploadCategories = lazy(() => import("@/pages/BulkUploadCategories"));
const NewOrder = lazy(() => import("@/pages/NewOrder"));
const OrderReceive = lazy(() => import("@/pages/OrderReceive"));
const BatchOrderCreate = lazy(() => import("@/pages/BatchOrderCreate"));
const OrderTemplates = lazy(() => import("@/pages/OrderTemplates"));
const DeliveryZones = lazy(() => import("@/pages/DeliveryZones"));
const ZoneLeadTimes = lazy(() => import("@/pages/ZoneLeadTimes"));
const Units = lazy(() => import("@/pages/Units"));
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const BranchTemplates = lazy(() => import("@/pages/BranchTemplates"));
const Transfers = lazy(() => import("@/pages/Transfers"));
const EnforcementSettings = lazy(() => import("@/pages/EnforcementSettings"));
const BranchInventory = lazy(() => import("@/pages/BranchInventory"));
const SafetyStocks = lazy(() => import("@/pages/SafetyStocks"));
const BulkUploadSafetyStocks = lazy(() => import("@/pages/BulkUploadSafetyStocks"));
const BulkUploadOpeningStock = lazy(() => import("@/pages/BulkUploadOpeningStock"));
const BulkUploadBranches = lazy(() => import("@/pages/BulkUploadBranches"));
const RoleManagement = lazy(() => import("@/pages/RoleManagement"));
const PeopleAccess = lazy(() => import("@/pages/PeopleAccess"));
const BranchProfile = lazy(() => import("@/pages/BranchProfile"));
const BranchComparison = lazy(() => import("@/pages/BranchComparison"));
const LaneFulfillment = lazy(() => import("@/pages/LaneFulfillment"));
const ItemOrders = lazy(() => import("@/pages/ItemOrders"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const BranchItemMatrix = lazy(() => import("@/pages/BranchItemMatrix"));
const ItemGroups = lazy(() => import("@/pages/ItemGroups"));
const Recipes = lazy(() => import("@/pages/Recipes"));
const IngredientsReview = lazy(() => import("@/pages/IngredientsReview"));
const BulkUploadItemGroups = lazy(() => import("@/pages/BulkUploadItemGroups"));
const BulkUploadItemConversions = lazy(() => import("@/pages/BulkUploadItemConversions"));
const BulkUploadUnits = lazy(() => import("@/pages/BulkUploadUnits"));
const Stocktakes = lazy(() => import("@/pages/Stocktakes"));
const StocktakeEntry = lazy(() => import("@/pages/StocktakeEntry"));
const CountSchedules = lazy(() => import("@/pages/CountSchedules"));
const Suppliers = lazy(() => import("@/pages/Suppliers"));
const DeliveryCompanies = lazy(() => import("@/components/delivery-companies/DeliveryCompanies"));
const SupplierDetail = lazy(() => import("@/pages/SupplierDetail"));
const BulkUploadSupplierItems = lazy(() => import("@/pages/BulkUploadSupplierItems"));
const PurchaseOrders = lazy(() => import("@/pages/PurchaseOrders"));
const PurchaseRequests = lazy(() => import("@/pages/PurchaseRequests"));
const RequestsToOrder = lazy(() => import("@/pages/RequestsToOrder"));
const InternalStockRequests = lazy(() => import("@/pages/InternalStockRequests"));
const SupplierReceipts = lazy(() => import("@/pages/SupplierReceipts"));
const SupplierReceiptQualityReview = () => <SupplierReceipts qualityOnly />;
const ProcurementSettings = lazy(() => import("@/pages/ProcurementSettings"));
const SupplierCycles = lazy(() => import("@/pages/SupplierCycles"));
const PendingPricing = lazy(() => import("@/pages/PendingPricing"));
const SupplierPayments = lazy(() => import("@/pages/SupplierPayments"));
const CycleInstanceDashboard = lazy(() => import("@/pages/CycleInstanceDashboard"));
const BranchSubmissionPage = lazy(() => import("@/pages/BranchSubmissionPage"));
const MyCycles = lazy(() => import("@/pages/MyCycles"));
const Returns = lazy(() => import("@/pages/Returns"));
const ReturnCreate = lazy(() => import("@/pages/ReturnCreate"));
const ReturnDetail = lazy(() => import("@/pages/ReturnDetail"));
const ReturnQuarantine = lazy(() => import("@/pages/ReturnQuarantine"));
const ReturnStats = lazy(() => import("@/pages/ReturnStats"));

const ReturnsWasteAnalytics = lazy(() => import("@/pages/ReturnsWasteAnalytics"));
const ReturnSettings = lazy(() => import("@/pages/ReturnSettings"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Signoffs = lazy(() => import("@/pages/Signoffs"));
const NotificationPreferences = lazy(() => import("@/pages/NotificationPreferences"));
const SystemAuditLog = lazy(() => import("@/pages/SystemAuditLog"));
const LogisticsCalendar = lazy(() => import("@/pages/LogisticsCalendar"));
const WasteLogs = lazy(() => import("@/pages/WasteLogs"));
const WasteReasons = lazy(() => import("@/pages/WasteReasons"));
const WasteSchedules = lazy(() => import("@/pages/WasteSchedules"));
const WasteAnalytics = lazy(() => import("@/pages/WasteAnalytics"));
const Customers = lazy(() => import("@/pages/Customers"));
const CustomerOrders = lazy(() => import("@/pages/CustomerOrders"));
const NewCustomerOrder = lazy(() => import("@/pages/NewCustomerOrder"));
const TransactionReport = lazy(() => import("@/pages/reports/TransactionReport"));
const ProcurementReport = lazy(() => import("@/pages/reports/ProcurementReport"));
const ReplenishmentReport = lazy(() => import("@/pages/reports/ReplenishmentReport"));
const SalesUsageReport = lazy(() => import("@/pages/reports/SalesUsageReport"));
const BrandMixReport = lazy(() => import("@/pages/reports/BrandMixReport"));
const CombinedOrderItemsReport = lazy(() => import("@/pages/reports/CombinedOrderItemsReport"));
const SalesUpload = lazy(() => import("@/pages/SalesUpload"));
const BuyerGroups = lazy(() => import("@/pages/BuyerGroups"));
const PriceLists = lazy(() => import("@/pages/PriceLists"));
const PriceListDetail = lazy(() => import("@/pages/PriceListDetail"));
const BulkUploadPriceList = lazy(() => import("@/pages/BulkUploadPriceList"));
const Catalog = lazy(() => import("@/pages/Catalog"));
const ApprovalBottleneck = lazy(() => import("@/pages/ApprovalBottleneck"));
const UniformPolicy = lazy(() => import("@/pages/UniformPolicy"));
const UniformPolicyNew = lazy(() => import("@/pages/UniformPolicyNew"));
const UniformPolicySettings = lazy(() => import("@/pages/UniformPolicySettings"));
const UniformPolicyExceptions = lazy(() => import("@/pages/UniformPolicyExceptions"));
const UniformGarments = lazy(() => import("@/pages/UniformGarments"));
const UniformGarmentCreate = lazy(() => import("@/pages/UniformGarments").then(module => ({ default: module.UniformGarmentCreate })));
const UniformEmployees = lazy(() => import("@/pages/UniformEmployees"));
import { maintenanceRoutes } from "@/components/maintenance/MaintenanceRoutes";
import {LegacyJobsRedirect} from "@/components/maintenance/LegacyJobsRedirect";
const Staff = lazy(() => import("@/pages/Staff"));
const StaffTeamCheckGlobal = lazy(() => import("@/components/staff/StaffTeamCheck").then(module=>({default:module.StaffTeamCheckGlobal})));
const StaffCashSettingsPage = lazy(() => import("@/components/staff/StaffCashSettingsPage"));
const UniformIssue = lazy(() => import("@/pages/UniformIssue"));
const UniformHandovers = lazy(() => import("@/pages/UniformHandovers"));
const UniformReplacements = lazy(() => import("@/pages/UniformReplacements"));
const UniformClearance = lazy(() => import("@/pages/UniformClearance"));
const UniformIssueRequests = lazy(() => import("@/pages/UniformIssueRequests"));
const UniformsDashboard = lazy(() => import("@/pages/UniformsDashboard"));

import { SoftLockProvider } from "@/components/SoftLockProvider";
import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";

function HomeRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/dashboard"); }, [setLocation]);
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

function DashboardRouter() {
  const { user, roles, isLoading } = useAuth();
  const pref = typeof window !== "undefined" ? localStorage.getItem("infrastock_home_page") : null;
  const hasPerm = (p: string) => {
    if (!user || !roles) return false;
    const role = roles.find((r: any) => r.id === user.roleId);
    return !!role?.permissions?.some((rp: any) => rp.id === p);
  };
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  const hasAnalytics = hasPerm("dashboard.analytics");
  const hasOps = hasPerm("dashboard.operations");
  if (pref === "analytics" && hasAnalytics) {
    return <AnalyticsDashboard />;
  }
  if (hasOps) {
    return <OperationsDashboard />;
  }
  if (hasAnalytics) {
    return <AnalyticsDashboard />;
  }
  return <OperationsDashboard />;
}

function SupplierReceiptsRouter() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const isPhoneBrowser = () => {
    if (typeof window === "undefined") return false;
    const mobileUserAgent = /Android.+Mobile|iPhone|iPod|IEMobile|Opera Mini/i.test(window.navigator.userAgent);
    return mobileUserAgent || window.matchMedia("(max-width: 767px)").matches;
  };
  const [isPhone, setIsPhone] = useState(isPhoneBrowser);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsPhone(isPhoneBrowser());
    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    if (!isPhone) return;
    const params = new URLSearchParams(search);
    const focusId = params.get("focus") || params.get("receiptId");
    if (focusId) {
      params.delete("focus");
      params.delete("receiptId");
      setLocation(`/m/supplier-receipts/${encodeURIComponent(focusId)}${params.size ? `?${params}` : ""}`);
    } else if (receiptPrefill(search)) {
      setLocation(`/m/supplier-receipts/new?${params}`);
    } else {
      setLocation("/m/supplier-receipts");
    }
  }, [isPhone, setLocation, search]);

  if (isPhone) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return <SupplierReceipts />;
}

export function PrivateRoute({
  component: Component,
  permission,
  permissions,
  redirectTo = "/dashboard",
}: {
  component: React.ComponentType;
  permission?: string;
  permissions?: string[];
  redirectTo?: string;
}) {
  const { user, roles, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !user) {
      setLocation("/login");
    }
  }, [user, isLoading, setLocation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return null;
  const requiredPermissions = permissions || (permission ? [permission] : []);
  if (requiredPermissions.length > 0 && !requiredPermissions.some((required) => hasPermission(user, required, roles))) {
    setLocation(redirectTo);
    return null;
  }
  
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    }>
      <Component />
    </Suspense>
  );
}

function LegacyConsumptionRedirect() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  useEffect(() => {
    setLocation(`/reports/sales-usage${search ? `?${search}` : ""}`, { replace: true });
  }, [search, setLocation]);
  return null;
}

function LegacyEmployeesRedirect() {
  const [,setLocation]=useLocation();
  const search=useSearch();
  useEffect(()=>{setLocation(`/staff/employees${search?`?${search}`:""}`,{replace:true})},[search,setLocation]);
  return null;
}

export function RecipeRoute({ component }: { component: React.ComponentType }) {
  return <PrivateRoute component={component} permission="inventory.recipes.manage" />;
}

function Router() {
  const [location] = useLocation();
  const { t } = useTranslation();

  // Public supplier portal — no auth, no sidebar
  if (location.startsWith("/s/")) {
    return (
      <Switch>
        <Route path="/s/:token" component={SupplierPortal} />
      </Switch>
    );
  }

  // Mobile PWA shell — no desktop sidebar, bottom-nav layout
  if (location === "/m" || location.startsWith("/m/")) {
    return (
      <MobileLayout>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">{t("authPages.loadingEllipsis")}</div>
        }>
          <Switch>
            <Route path="/m/people-access" component={PeopleAccess} />
            <Route path="/m/people-access/*" component={PeopleAccess} />
            <Route path="/m" component={MobileHome} />
            <Route path="/m/inbox" component={MobileInbox} />
            <Route path="/m/purchase-orders">
              <PrivateRoute component={PurchaseOrders} permissions={["purchase_orders.view", "purchase_orders.create", "procurement.approve_po", "finance.approve_po"]} redirectTo="/m" />
            </Route>
            <Route path="/m/waste/analytics">
              <PrivateRoute component={MobileWasteAnalytics} permission="waste.view_analytics" redirectTo="/m" />
            </Route>
            <Route path="/m/waste">
              <PrivateRoute component={MobileWasteLogs} permissions={["waste.log", "waste.view_all", "waste.approve"]} redirectTo="/m" />
            </Route>
            <Route path="/m/supplier-receipts/new" component={MobileSupplierReceiptForm} />
            <Route path="/m/supplier-receipts/:id/edit" component={MobileSupplierReceiptForm} />
            <Route path="/m/supplier-receipts/:id" component={MobileSupplierReceiptDetail} />
            <Route path="/m/supplier-receipts" component={MobileSupplierReceipts} />
            <Route path="/m/inbound">
              <PrivateRoute component={MobileInbound} permission="orders.deliver" redirectTo="/m" />
            </Route>
            <Route path="/m/inbound/order/:id/propose">
              <PrivateRoute component={MobileInboundOrderPropose} permission="orders.deliver" redirectTo="/m" />
            </Route>
            <Route path="/m/inbound/order/:id">
              <PrivateRoute component={MobileInboundOrderDetail} permission="orders.deliver" redirectTo="/m" />
            </Route>
            <Route path="/m/inbound/transfer/:id">
              <PrivateRoute component={MobileDeepLink} permission="transfers.receive" redirectTo="/m" />
            </Route>
            <Route path="/m/inbound/return/:id">
              <PrivateRoute component={MobileDeepLink} permission="returns.receive" redirectTo="/m" />
            </Route>
            <Route path="/m/receive/:type/:ref" component={MobileReceiveDelivery} />
            <Route path="/m/profile/sync" component={MobileSync} />
            <Route path="/m/profile" component={MobileProfile} />
            <Route path="/m/personal-floats" component={MyPersonalFloats} />
            <Route path="/m/scan">
              <PrivateRoute component={MobileScan} permission="inventory.view" redirectTo="/m" />
            </Route>
            <Route path="/m/items">
              <PrivateRoute component={MobileItems} permission="inventory.items_page.view" redirectTo="/m" />
            </Route>
            <Route path="/m/stocktake/:id">
              <PrivateRoute component={MobileStocktakeCount} permission="stocktake.view" redirectTo="/m" />
            </Route>
            <Route path="/m/stocktake">
              <PrivateRoute component={MobileStocktakeList} permission="stocktake.view" redirectTo="/m" />
            </Route>
            <Route component={MobileHome} />
          </Switch>
        </Suspense>
      </MobileLayout>
    );
  }

  return (
    <Layout>
      <Switch>
        <Route path="/people-access">
          <PrivateRoute component={PeopleAccess} />
        </Route>
        <Route path="/people-access/*">
          <PrivateRoute component={PeopleAccess} />
        </Route>
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/dashboard/operations">
          <PrivateRoute component={OperationsDashboard} />
        </Route>
        <Route path="/dashboard/analytics">
          <PrivateRoute component={AnalyticsDashboard} permission="dashboard.analytics" />
        </Route>
        <Route path="/dashboard">
          <PrivateRoute component={DashboardRouter} />
        </Route>
        <Route path="/notifications">
          <PrivateRoute component={Notifications} />
        </Route>
        <Route path="/signoffs">
          <PrivateRoute component={Signoffs} />
        </Route>
        <Route path="/inventory">
          <PrivateRoute component={Inventory} permission="inventory.items_page.view" />
        </Route>
        <Route path="/inventory/new">
          <PrivateRoute component={ItemFormPage} permission="inventory.items_page.view" />
        </Route>
        <Route path="/inventory/bulk-upload">
          <PrivateRoute component={BulkUploadItems} permission="inventory.items_page.view" />
        </Route>
        <Route path="/inventory/returns/new">
          <PrivateRoute component={ReturnCreate} permission="returns.create" />
        </Route>
        <Route path="/inventory/returns/quarantine">
          <PrivateRoute component={ReturnQuarantine} permission="returns.receive" />
        </Route>
        <Route path="/inventory/returns/stats">
          <PrivateRoute component={ReturnStats} permission="returns.view_stats" />
        </Route>
        <Route path="/inventory/returns-waste/analytics">
          <PrivateRoute component={ReturnsWasteAnalytics} permission="analytics.returns_waste.view" />
        </Route>
        <Route path="/inventory/returns/:id">
          <PrivateRoute component={ReturnDetail} permission="returns.view" />
        </Route>
        <Route path="/inventory/returns">
          <PrivateRoute component={Returns} permission="returns.view" />
        </Route>
        <Route path="/inventory/waste/reasons">
          <PrivateRoute component={WasteReasons} permission="waste.manage_reasons" />
        </Route>
        <Route path="/inventory/waste/schedules">
          <PrivateRoute component={WasteSchedules} permission="waste.manage_schedules" />
        </Route>
        <Route path="/inventory/waste/analytics">
          <PrivateRoute component={WasteAnalytics} permission="waste.view_analytics" />
        </Route>
        <Route path="/inventory/waste">
          <PrivateRoute component={WasteLogs} permissions={["waste.log", "waste.view_all", "waste.approve"]} />
        </Route>
        <Route path="/inventory/safety-stocks/upload">
          <PrivateRoute component={BulkUploadSafetyStocks} permission="inventory.manage_safety_stock" />
        </Route>
        <Route path="/inventory/opening-stock/upload">
          <PrivateRoute component={BulkUploadOpeningStock} permission="inventory.adjust" />
        </Route>
        <Route path="/inventory/safety-stocks">
          <PrivateRoute component={SafetyStocks} permission="inventory.manage_safety_stock" />
        </Route>
        <Route path="/inventory/recipes">
          <RecipeRoute component={Recipes} />
        </Route>
        <Route path="/inventory/ingredients-review">
          <PrivateRoute component={IngredientsReview} permission="inventory.recipes.manage" />
        </Route>
        <Route path="/sales-upload">
          <PrivateRoute component={SalesUpload} permission="sales.upload" />
        </Route>
        <Route path="/inventory/:id/edit">
          <PrivateRoute component={ItemFormPage} permission="inventory.items_page.view" />
        </Route>
        <Route path="/procurement/item-brands">
          <PrivateRoute component={ItemBrandsPage} />
        </Route>
        <Route path="/inventory/:id">
          <PrivateRoute component={ProductDetails} permission="inventory.items_page.view" />
        </Route>
        <Route path="/orders/templates">
          <PrivateRoute component={OrderTemplates} />
        </Route>
        <Route path="/orders/branch-templates">
          <PrivateRoute component={BranchTemplates} />
        </Route>
        <Route path="/orders/delivery-zones">
          <PrivateRoute component={DeliveryZones} />
        </Route>
        <Route path="/orders/zone-lead-times">
          <PrivateRoute component={ZoneLeadTimes} />
        </Route>
        <Route path="/orders/batch-create">
          <PrivateRoute component={BatchOrderCreate} />
        </Route>
        <Route path="/orders/new">
          <PrivateRoute component={NewOrder} />
        </Route>
        <Route path="/orders/bulk-upload">
          <PrivateRoute component={BulkOrderUpload} />
        </Route>
        <Route path="/orders/:id/receive">
          <PrivateRoute component={OrderReceive} />
        </Route>
        <Route path="/orders/:id">
          <PrivateRoute component={Orders} />
        </Route>
        <Route path="/orders">
          <PrivateRoute component={Orders} />
        </Route>
        <Route path="/transfers">
          <PrivateRoute component={Transfers} permission="transfers.view" />
        </Route>
        <Route path="/branch-inventory">
          <PrivateRoute component={BranchInventory} permission="inventory.view" />
        </Route>
        <Route path="/branch-profile">
          <PrivateRoute component={BranchProfile} />
        </Route>
        <Route path="/branch-comparison">
          <PrivateRoute component={BranchComparison} permission="analytics.branch_comparison" />
        </Route>
        <Route path="/analytics/lane-fulfillment">
          <PrivateRoute component={LaneFulfillment} permission="analytics.lane_fulfillment" />
        </Route>
        <Route path="/item-orders/:itemId">
          <PrivateRoute component={ItemOrders} permission="analytics.item_orders" />
        </Route>
        <Route path="/activity-log">
          <PrivateRoute component={ActivityLog} permission="analytics.activity_log" />
        </Route>
        <Route path="/branch-item-matrix">
          <PrivateRoute component={BranchItemMatrix} />
        </Route>
        <Route path="/financials/branch-cash/expense-setup">
          <PrivateRoute component={ExpenseSetup} permissions={["branch_cash.view", "expense_setup.manage"]} />
        </Route>
        <Route path="/financials/branch-cash/cash-book">
          <PrivateRoute component={() => <BranchCash page="cash-book" />} />
        </Route>
        <Route path="/financials/branch-cash/where-it-went">
          <PrivateRoute component={() => <BranchCash page="where-it-went" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/collections">
          <PrivateRoute component={() => <BranchCash page="collections" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/jobs">
          <PrivateRoute component={LegacyJobsRedirect} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/personal-floats">
          <PrivateRoute component={() => <BranchCash page="personal-floats" />} />
        </Route>
        <Route path="/financials/branch-cash/who-holds-cash">
          <PrivateRoute component={() => <BranchCash page="who-holds-cash" />} />
        </Route>
        <Route path="/financials/branch-cash/things-to-fix">
          <PrivateRoute component={() => <BranchCash page="things-to-fix" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/insights">
          <PrivateRoute component={() => <BranchCash page="insights" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/settings">
          <PrivateRoute component={StaffCashSettingsPage} permission="branch_cash.manage" />
        </Route>
        <Route path="/my-personal-floats">
          <PrivateRoute component={MyPersonalFloats} />
        </Route>
        <Route path="/financials/branch-cash/daily-counts">
          <PrivateRoute component={() => <BranchCash page="daily-counts" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials/branch-cash/cash-boxes">
          <PrivateRoute component={() => <BranchCash page="cash-boxes" />} />
        </Route>
        <Route path="/financials/branch-cash">
          <PrivateRoute component={() => <BranchCash page="overview" />} permission="branch_cash.view" />
        </Route>
        <Route path="/financials">
          <PrivateRoute component={() => <Financials view="overview" />} />
        </Route>
        <Route path="/financials/orders">
          <PrivateRoute component={() => <Financials view="orders" />} />
        </Route>
        <Route path="/financials/audit">
          <PrivateRoute component={() => <Financials view="audit" />} />
        </Route>
        <Route path="/admin/users">
          <PrivateRoute component={Admin} />
        </Route>
        <Route path="/admin/roles">
          <PrivateRoute component={Admin} />
        </Route>
        <Route path="/admin/locations/bulk-upload">
          <PrivateRoute component={BulkUploadBranches} permission="locations.manage" />
        </Route>
        <Route path="/admin/locations">
          <PrivateRoute component={Admin} permission="locations.manage" />
        </Route>
        <Route path="/admin/cutoffs">
          <PrivateRoute component={Admin} />
        </Route>
        <Route path="/admin/price-reasons">
          <PrivateRoute component={PriceChangeReasons} />
        </Route>
        <Route path="/admin/categories">
          <PrivateRoute component={() => <Categories scope="general" />} />
        </Route>
        <Route path="/admin/finished-product-categories">
          <PrivateRoute component={() => <Categories scope="finished_product" />} />
        </Route>
        <Route path="/admin/categories/bulk-upload">
          <PrivateRoute component={BulkUploadCategories} />
        </Route>
        <Route path="/admin/brands">
          <PrivateRoute component={Brands} />
        </Route>
        <Route path="/admin/vehicles">
          <PrivateRoute component={Vehicles} />
        </Route>
        <Route path="/admin/feature-flags">
          <PrivateRoute component={RulesFeatureSettings} />
        </Route>
        <Route path="/admin/rules">
          <PrivateRoute component={RulesList} />
        </Route>
        <Route path="/admin/rules/audit">
          <PrivateRoute component={RulesAudit} />
        </Route>
        <Route path="/admin/rules/new">
          <PrivateRoute component={RuleEditor} />
        </Route>
        <Route path="/admin/rules/:id">
          <PrivateRoute component={RuleEditor} />
        </Route>
        <Route path="/admin/units/bulk-upload">
          <PrivateRoute component={BulkUploadUnits} />
        </Route>
        <Route path="/admin/units">
          <PrivateRoute component={Units} />
        </Route>
        <Route path="/admin/merge-duplicates">
          <PrivateRoute component={AdminMergeDuplicates} />
        </Route>
        <Route path="/admin/approval-bottleneck">
          <PrivateRoute component={ApprovalBottleneck} />
        </Route>
        <Route path="/super-admin">
          <PrivateRoute component={SuperAdmin} />
        </Route>
        <Route path="/settings/enforcement">
          <PrivateRoute component={EnforcementSettings} />
        </Route>
        <Route path="/settings/roles">
          <PrivateRoute component={RoleManagement} />
        </Route>
        <Route path="/settings/item-groups">
          <PrivateRoute component={ItemGroups} />
        </Route>
        <Route path="/settings/item-groups/bulk-upload">
          <PrivateRoute component={BulkUploadItemGroups} />
        </Route>
        <Route path="/inventory/item-conversions/bulk-upload">
          <PrivateRoute component={BulkUploadItemConversions} />
        </Route>
        <Route path="/settings/count-schedules">
          <PrivateRoute component={CountSchedules} permission="stocktake.manage_schedule" />
        </Route>
        <Route path="/stocktakes/:id">
          <PrivateRoute component={StocktakeEntry} permission="stocktake.view" />
        </Route>
        <Route path="/stocktakes">
          <PrivateRoute component={Stocktakes} permission="stocktake.view" />
        </Route>
        <Route path="/suppliers/bulk-link-items">
          <PrivateRoute component={BulkUploadSupplierItems} />
        </Route>
        <Route path="/suppliers/:id">
          <PrivateRoute component={SupplierDetail} />
        </Route>
        <Route path="/suppliers">
          <PrivateRoute component={Suppliers} />
        </Route>
        <Route path="/delivery-companies">
          <PrivateRoute component={DeliveryCompanies} />
        </Route>
        <Route path="/delivery-companies/:id">
          <PrivateRoute component={DeliveryCompanies} />
        </Route>
        <Route path="/purchase-requests">
          <PrivateRoute component={PurchaseRequests} permissions={["purchase_requests.view", "purchase_requests.process", "purchase_requests.view_all"]} />
        </Route>
        <Route path="/procurement/requests-to-order">
          <PrivateRoute component={RequestsToOrder} permission="purchase_requests.process" />
        </Route>
        <Route path="/internal-stock-requests">
          <PrivateRoute component={InternalStockRequests} />
        </Route>
        <Route path="/purchase-orders">
          <PrivateRoute component={PurchaseOrders} />
        </Route>
        <Route path="/supplier-receipts">
          <PrivateRoute component={SupplierReceiptsRouter} />
        </Route>
        <Route path="/procurement/receipt-quality">
          <PrivateRoute component={SupplierReceiptQualityReview} permission="supplier_receipts.view" />
        </Route>
        <Route path="/procurement/pending-pricing">
          <PrivateRoute component={PendingPricing} permission="supplier_receipts.edit_confirmed" />
        </Route>
        <Route path="/procurement/supplier-payments">
          <PrivateRoute component={SupplierPayments} permission="suppliers.payments.view" />
        </Route>
        <Route path="/supplier-receipts/changes">
          <PrivateRoute component={ReceiptChanges} permission="supplier_receipts.review_change_requests" />
        </Route>
        <Route path="/settings/procurement">
          <PrivateRoute component={ProcurementSettings} permission="procurement_settings.manage" />
        </Route>
        <Route path="/supplier-cycles/my-cycles">
          <PrivateRoute component={MyCycles} />
        </Route>
        <Route path="/supplier-cycles/instances/:instanceId">
          <PrivateRoute component={CycleInstanceDashboard} />
        </Route>
        <Route path="/supplier-cycles/:cycleId/instances/:instanceId/submit">
          <PrivateRoute component={BranchSubmissionPage} />
        </Route>
        <Route path="/supplier-cycles">
          <PrivateRoute component={SupplierCycles} />
        </Route>
        <Route path="/settings/returns">
          <PrivateRoute component={ReturnSettings} permission="returns.manage_settings" />
        </Route>
        <Route path="/settings/notifications">
          <PrivateRoute component={NotificationPreferences} />
        </Route>
        <Route path="/settings">
          <PrivateRoute component={Settings} />
        </Route>
        <Route path="/logistics">
          <PrivateRoute component={LogisticsCalendar} permission="logistics.view_calendar" />
        </Route>
        <Route path="/logistics/calendar">
          <PrivateRoute component={LogisticsCalendar} permission="logistics.view_calendar" />
        </Route>
        <Route path="/system/audit-log">
          <PrivateRoute component={SystemAuditLog} />
        </Route>
        <Route path="/customers">
          <PrivateRoute component={Customers} />
        </Route>
        <Route path="/customer-orders/new">
          <PrivateRoute component={NewCustomerOrder} />
        </Route>
        <Route path="/customer-orders">
          <PrivateRoute component={CustomerOrders} />
        </Route>
        <Route path="/reports/transactions">
          <PrivateRoute component={TransactionReport} />
        </Route>
        <Route path="/reports/combined-order-items">
          <PrivateRoute component={CombinedOrderItemsReport} />
        </Route>
        <Route path="/reports/procurement">
          <PrivateRoute component={ProcurementReport} />
        </Route>
        <Route path="/reports/replenishment">
          <PrivateRoute component={ReplenishmentReport} />
        </Route>
        <Route path="/reports/sales-usage">
          <PrivateRoute component={SalesUsageReport} permission="reports.consumption.view" />
        </Route>
        <Route path="/reports/brand-mix">
          <PrivateRoute component={BrandMixReport} permission="reports.consumption.view" />
        </Route>
        <Route path="/reports/consumption">
          <PrivateRoute component={LegacyConsumptionRedirect} permission="reports.consumption.view" />
        </Route>
        <Route path="/internal-market/buyer-groups">
          <PrivateRoute component={BuyerGroups} />
        </Route>
        <Route path="/internal-market/price-lists/:id/upload">
          <PrivateRoute component={BulkUploadPriceList} />
        </Route>
        <Route path="/internal-market/price-lists/:id">
          <PrivateRoute component={PriceListDetail} />
        </Route>
        <Route path="/internal-market/price-lists">
          <PrivateRoute component={PriceLists} />
        </Route>
        <Route path="/catalog">
          <PrivateRoute component={Catalog} />
        </Route>
        <Route path="/uniforms">
          <PrivateRoute component={UniformsDashboard} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/policy/new">
          <PrivateRoute component={UniformPolicyNew} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/policy/settings">
          <PrivateRoute component={UniformPolicySettings} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/policy/exceptions">
          <PrivateRoute component={UniformPolicyExceptions} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/policy/:id">
          <PrivateRoute component={UniformPolicy} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/policy">
          <PrivateRoute component={UniformPolicy} permission="uniforms.view" />
        </Route>
         <Route path="/uniforms/issue-requests">
           <PrivateRoute component={UniformIssueRequests} permission="uniforms.approve_over_cap" />
         </Route>
         <Route path="/uniforms/issue">
           <PrivateRoute component={UniformIssue} permission="uniforms.view" />
         </Route>
         <Route path="/uniforms/handovers">
           <PrivateRoute component={UniformHandovers} permission="uniforms.view" />
         </Route>
         <Route path="/uniforms/replacements">
           <PrivateRoute component={UniformReplacements} permission="uniforms.view" />
         </Route>
         <Route path="/uniforms/clearance">
           <PrivateRoute component={UniformClearance} permission="uniforms.view" />
         </Route>
        <Route path="/uniforms/garments/new">
          <PrivateRoute component={UniformGarmentCreate} />
        </Route>
        <Route path="/uniforms/garments">
          <PrivateRoute component={UniformGarments} permission="uniforms.view" />
        </Route>
        <Route path="/uniforms/employees">
          <PrivateRoute component={LegacyEmployeesRedirect} permission="employees.view" />
        </Route>
        {maintenanceRoutes(PrivateRoute)}
        <Route path="/staff/employees/tools">
          <PrivateRoute component={UniformEmployees} permission="employees.view" />
        </Route>
        <Route path="/staff">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/employees">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/attendance">
          <PrivateRoute component={Staff} permission="attendance.view" />
        </Route>
        <Route path="/staff/turnover">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/positions">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/alerts">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/activity">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/pay">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/pay-records">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/bonuses">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/certificates-overview">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/certificates">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/advances">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/deductions">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/deductions/add">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/deductions/new">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/staff/deductions/:id">
          <PrivateRoute component={Staff} permission="employees.view" />
        </Route>
        <Route path="/">
          <HomeRedirect />
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("React Error Boundary caught:", error, info);
    // A failed dynamic import usually means a new version was published and the
    // old hashed chunk is gone. Refresh onto the latest assets instead of
    // stranding the user on an error screen.
    if (isChunkLoadError(error)) {
      void reloadForFreshShell("error-boundary-chunk");
      return;
    }
    try {
      const payload: Record<string, string> = {
        msg: 'ErrorBoundary: ' + String(error.message || error),
        stack: String(error.stack || ''),
        componentStack: String(info.componentStack || ''),
        url: String(window.location.href),
        ua: String(navigator.userAgent),
      };
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
    } catch(e) {}
  }
  render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return (
          <div style={{ padding: 40, fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
            <p style={{ marginTop: 16, color: "#475569" }}>{i18n.language === "ar" ? "يرجى تحديث الصفحة. تعذّر تحميل أحد ملفاتها." : "Please refresh. A page file could not load."}</p>
          </div>
        );
      }
      return (
        <div style={{ padding: 40, fontFamily: "monospace" }}>
          <h1 style={{ color: "red" }}>{i18n.t("authPages.appError")}</h1>
          <pre style={{ whiteSpace: "pre-wrap", marginTop: 16 }}>{this.state.error?.message}</pre>
          <pre style={{ whiteSpace: "pre-wrap", marginTop: 8, fontSize: 12, color: "#666" }}>{this.state.error?.stack}</pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: "8px 16px" }}>{i18n.t("authPages.reload")}</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <Toaster />
            <Suspense fallback={null}><CashPinGateProvider /></Suspense>
            <ReceiptDuplicateEntry />
            <SoftLockProvider />
            <Suspense fallback={null}><StaffTeamCheckGlobal /></Suspense>
            <Router />
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
